import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CalendarDays, Settings, Bell, Mail, ShieldCheck, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TravelSettings = () => {
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCalendar();
  }, []);

  const fetchCalendar = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/travel-desk/calendar`, { withCredentials: true });
      setCalendarEvents(data.events || []);
    } catch (err) {
      toast.error('Failed to load travel calendar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto font-sans">
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-amber-500/20 text-amber-400 rounded-xl border border-amber-500/30">
            <CalendarDays className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-black">Travel Settings &amp; Calendar</h1>
            <p className="text-xs text-blue-200">
              Notification preferences, workflow rules, template configurations &amp; corporate travel calendar.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calendar Card */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4">
          <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2 border-b border-slate-200 pb-3">
            <CalendarDays className="w-4 h-4 text-blue-600" /> Upcoming Corporate Trips Calendar
          </h3>

          <div className="space-y-2.5 max-h-[400px] overflow-y-auto pr-1">
            {calendarEvents.length === 0 ? (
              <p className="text-xs text-slate-400 italic">No scheduled upcoming trips found in calendar.</p>
            ) : (
              calendarEvents.map((evt, idx) => (
                <div key={idx} className="p-3 bg-slate-50 rounded-xl border border-slate-100 text-xs flex justify-between items-center">
                  <div>
                    <div className="font-bold text-slate-900">{evt.title}</div>
                    <div className="text-slate-500 text-[11px]">{evt.department}</div>
                  </div>
                  <span className="font-mono font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded">
                    {evt.start}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Configuration Panel */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4">
          <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2 border-b border-slate-200 pb-3">
            <Settings className="w-4 h-4 text-blue-600" /> System Configurations &amp; Templates
          </h3>

          <div className="space-y-3 text-xs">
            <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
              <div className="font-bold text-slate-900">Email &amp; SMS Notifications</div>
              <div className="text-slate-500">Auto-trigger notifications to employee &amp; approvers at each stage.</div>
            </div>

            <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
              <div className="font-bold text-slate-900">Auto-Approval Thresholds</div>
              <div className="text-slate-500">Requests under ₹ 5,000 for domestic travel skip multi-tier routing.</div>
            </div>

            <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
              <div className="font-bold text-slate-900">Corporate Document Templates</div>
              <div className="text-slate-500">Custom header, footer, logo &amp; watermarks for Travel Approval Receipt PDF.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TravelSettings;
